import { Button, Modal, Uploader, Loader } from "rsuite";
import styles from "./FileUploaderModal.module.css";
import React, { useState } from "react";
import axios from "axios";
import ImageIcon from "@rsuite/icons/Image";
import FileUploadIcon from "@rsuite/icons/FileUpload";
import WarningRoundIcon from "@rsuite/icons/WarningRound";
import { showToast } from "Components/Toast";

const DEFAULT_MAX_SIZE_MB = 8;
const DEFAULT_MAX_FILES = 10;

/**
 * Shared file-upload modal. Encapsulates drag/drop, size + count validation,
 * the uploading state, inline errors, and the multipart POST. The call site
 * supplies the endpoint, any extra form fields, and an onUploaded callback
 * that receives the server response (so each module wires its own store).
 *
 * Props:
 *   display      (bool)              modal open state
 *   onClose      (fn)                close handler
 *   uploadPath   (string)           full upload URL
 *   onUploaded   (fn(data))         called with response.data on success
 *   extraFields  (object)           extra FormData fields, e.g. { locationId }
 *   maxFiles     (number)           default 10
 *   maxSizeMb    (number)           default 8
 *   multiple     (bool)             default true
 *   title        (string)           modal title
 *   subtitle     (string)           modal subtitle
 *   successMessage (string)         toast on success
 */
function FileUploaderModal({
  display,
  onClose,
  uploadPath,
  onUploaded,
  extraFields,
  maxFiles = DEFAULT_MAX_FILES,
  maxSizeMb = DEFAULT_MAX_SIZE_MB,
  multiple = true,
  title = "Upload Files",
  subtitle = "Select files to upload",
  successMessage = "Files uploaded successfully",
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [fileList, setFileList] = React.useState([]);
  const uploader = React.useRef();

  const maxFileSize = maxSizeMb * 1024 * 1024;

  // Validate against size + count limits. Returns the accepted files and
  // sets an inline error message describing what was rejected (if any).
  const validateFiles = (incoming, existingCount) => {
    const accepted = [];
    const oversized = [];
    let overflow = 0;

    incoming.forEach((file) => {
      const size = file?.blobFile?.size ?? file?.size ?? 0;
      if (size > maxFileSize) {
        oversized.push(file?.name || file?.blobFile?.name || "file");
        return;
      }
      if (existingCount + accepted.length >= maxFiles) {
        overflow += 1;
        return;
      }
      accepted.push(file);
    });

    const messages = [];
    if (oversized.length) messages.push(`Max file size is ${maxSizeMb}MB. Skipped: ${oversized.join(", ")}`);
    if (overflow) {
      messages.push(maxFiles === 1 ? "You can upload only 1 file." : `You can upload up to ${maxFiles} files at once.`);
    }

    setError(messages.join(" "));

    return accepted;
  };

  // Uploader's own click-to-browse passes its full file list here. For
  // single-file mode keep only the most recent selection.
  const handleChange = (files) => {
    const incoming = multiple ? files : files.slice(-1);
    setFileList(validateFiles(incoming, 0));
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();

    const dropped = Array.from(event.dataTransfer.files)
      .slice(multiple ? 0 : -1)
      .map((file) => ({ name: file.name, blobFile: file }));

    if (multiple) {
      setFileList((prev) => [...prev, ...validateFiles(dropped, prev.length)]);
    } else {
      setFileList(validateFiles(dropped, 0));
    }
  };

  const preventDefault = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleClose = () => {
    setFileList([]);
    setError("");
    onClose();
  };

  const uploadFiles = async () => {
    setLoading(true);

    const formData = new FormData();
    fileList.forEach((file) => {
      // 'file' field name kept for multer compatibility across endpoints.
      formData.append("file", file.blobFile, file.blobFile.name);
    });
    if (extraFields) {
      Object.entries(extraFields).forEach(([key, value]) => {
        if (value !== undefined && value !== null) formData.append(key, value);
      });
    }

    try {
      const response = await axios.post(uploadPath, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        withCredentials: true,
      });

      if (response.status === 200) {
        if (onUploaded) onUploaded(response.data);
        if (successMessage) showToast("s", successMessage);
        setFileList([]);
        setError("");
        onClose();
      }
    } catch (err) {
      const message = err?.response
        ? err.response.data?.message || `Upload failed (${err.response.status})`
        : "Cannot reach the server. Please check your connection and try again.";
      showToast("e", message);
      console.error("Error uploading files:", err);
    }

    setLoading(false);
  };

  const fileWord = multiple ? "files" : "file";

  return (
    <Modal size="sm" open={display} onClose={handleClose} className={styles.modal}>
      <Modal.Header>
        <Modal.Title>
          <div className={styles.title}>{title}</div>
          <div className={styles.subtitle}>{subtitle}</div>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className={styles.uploading_state}>
            <Loader size="md" />
            <p className={styles.uploading_title}>Uploading {fileWord}...</p>
            <p className={styles.uploading_hint}>Please wait while we upload your {fileWord}</p>
          </div>
        ) : (
          <>
            <div onDragOver={preventDefault} onDragEnter={preventDefault} onDrop={handleDrop}>
              <Uploader
                ref={uploader}
                onChange={handleChange}
                action={uploadPath}
                multiple={multiple}
                listType={"picture-text"}
                autoUpload={false}
                name="file"
                fileList={fileList}
                className={styles.uploader}
              >
                <div className={styles.dropzone}>
                  <div className={styles.dropzone_icon}>
                    <ImageIcon />
                  </div>
                  <div className={styles.dropzone_title}>Drop your {fileWord} here</div>
                  <div className={styles.dropzone_hint}>Any file type (max. {maxSizeMb}MB)</div>
                  <span className={styles.select_btn}>
                    <FileUploadIcon /> Select {fileWord}
                  </span>
                </div>
              </Uploader>
            </div>

            {error && (
              <div className={styles.error_row} role="alert">
                <WarningRoundIcon />
                <span>{error}</span>
              </div>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <div className={styles.footer}>
          <Button onClick={handleClose} appearance="subtle" disabled={loading}>
            Cancel
          </Button>
          <Button loading={loading} disabled={!fileList.length} onClick={uploadFiles} color="blue" appearance="primary">
            {fileList.length ? `Upload ${fileList.length} ${fileList.length > 1 ? "files" : "file"}` : "Upload"}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}

export default FileUploaderModal;
